import { useMemo } from 'react'
import { Info } from 'lucide-react'
import { useContextSources } from '@/hooks'

interface BaseBranchHintProps {
  projectId: string
  mapValue: string
  repoId: string
}

export function BaseBranchHint({
  projectId,
  mapValue,
  repoId,
}: Readonly<BaseBranchHintProps>) {
  const { data: contextData } = useContextSources(
    projectId,
    { maxResultCount: 1000 },
    undefined,
    { enabled: !!repoId },
  )
  const items = contextData?.items

  const { branch, repoName } = useMemo(() => {
    const empty = { branch: '', repoName: undefined as string | undefined }
    if (!mapValue || !repoId) return empty

    let map: Record<string, string>
    try {
      map = JSON.parse(mapValue) as Record<string, string>
    } catch {
      return empty
    }

    const targetName = items?.find((s) => s.id === repoId)?.name

    let resolved = map[repoId] ?? ''
    if (!resolved && targetName && items) {
      for (const [keyId, value] of Object.entries(map)) {
        if (value && items.find((s) => s.id === keyId)?.name === targetName) {
          resolved = value
          break
        }
      }
    }

    return { branch: resolved, repoName: targetName }
  }, [mapValue, repoId, items])

  if (!branch) return null

  return (
    <p
      className="flex items-start gap-1 mt-1 text-xs leading-[1.6] tracking-[0.015em] text-button"
      data-testid="base-branch-hint"
    >
      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
      <span>
        This feature branch will be created from <span className="font-medium">{branch}</span>
        {repoName ? <> (the branch selected for {repoName})</> : null}.
      </span>
    </p>
  )
}
